import React, { useState, useRef, forwardRef } from 'react';
import PropTypes from 'prop-types';
import { cx } from '@linaria/core';
import Title from '../title/index.jsx';
import { useLocalization } from 'contexts/LocalizationContext.jsx';
import {
  colors,
  rootClassname,
  inputContainerClassname,
  inputContainerFocused,
  inputContainerDisabled,
  inputClassname,
  leftElementContainer,
  rightElementContainer,
  inputWithError,
  inputWithWarning,
  inputDisabled,
  errorClassname,
  warningClassname,
} from './styles.js';

const Input = forwardRef(({
  type = 'text',
  label = null,
  labelType = 'h6',
  error = null,
  action = null,
  useLocalValue = true,
  elementRight = null,
  elementLeft = null,
  style: overwriteStyles = {},
  warning = null,
  className: propClassName,
  leftStyle = {},
  leftClassName,
  rightStyle = {},
  rightClassName,
  dataTestId = null,
  rootStyle = {},
  disabled = false,
  containerStyle = {},
  autoFocus = false,
  positiveOnly = false,
  ...attr
}, ref) => {
  const { labels } = useLocalization(getLabels);
  const [focus, setFocus] = useState(false);
  const [localValue, setLocalValue] = useState(null);
  const className = cx(inputClassname, disabled && inputDisabled, propClassName);
  const inputContainer = cx(
    inputContainerClassname,
    focus && inputContainerFocused,
    error && inputWithError,
    warning && inputWithWarning,
    disabled && inputContainerDisabled,
  );
  const { value, onFocus, onChange, onBlur } = attr;
  const inputReference = ref || useRef(null);
  const [errorState, setErrorState] = useState(null);

  const handleFocus = event => {
    if (typeof onFocus === 'function') onFocus(event);
    event.target.select();
    setLocalValue(event.target.value);
    setFocus(true);
  };

  const handleChange = event => {
    const { target: { value, min, max, type } } = event;
    if (typeof onChange === 'function') {
      const errorMessage = (() => {
        if (!['number', 'range', 'date', 'datetime-local', 'month', 'time', 'week'].includes(type)) return; // Only verify for supported types
        if (positiveOnly && value < 0) return labels.valueGreaterThanOrEqualErrorMessage(0); // Only positive values
        if ((!min || value >= min) && (!max || value <= max)) return; // Values is between set min/max values
        if (min && value < min) return labels.valueGreaterThanOrEqualErrorMessage(min);
        if (max && value > max) return labels.valueLessThanOrEqualErrorMessage(max);
        return labels.valueBetweenErrorMessage(min, max);
      })();
      setErrorState(errorMessage);
      if (!errorMessage) onChange(event);
    }
    setLocalValue(value);
  };

  const handleBlur = event => {
    if (typeof onBlur === 'function') onBlur(event);
    setLocalValue(null);
    setFocus(false);
  };

  const inputclick = () => {
    if (!!action) return;
    inputReference?.current?.focus();
  };

  const input = (
    <input
      type={type}
      className={className}
      style={overwriteStyles}
      {...attr}
      value={focus && useLocalValue ? localValue : value}
      onFocus={handleFocus}
      onChange={handleChange}
      onBlur={handleBlur}
      ref={inputReference}
      data-test-id={dataTestId}
      disabled={disabled}
      autoFocus={autoFocus}
    />
  );

  const rightElement = action || elementRight;
  const localError = error || errorState;

  return (
    <div className={rootClassname} style={rootStyle}>
      {label && <Title type={labelType} style={{ ...disabled && { color: colors.disabled } }}>{label}</Title>}
      <div style={containerStyle} className={inputContainer} onClick={inputclick}>
        {!!elementLeft && <div className={cx(leftElementContainer, leftClassName )} style={leftStyle}>{elementLeft}</div>}
        {input}
        {!!rightElement && <div className={cx(rightElementContainer, rightClassName, 'input-icon-right')} style={rightStyle}>{rightElement}</div>}
      </div>
      {!!localError && <p className={errorClassname}>{localError}</p>}
      {warning && <p className={warningClassname}>{warning}</p>}
    </div>
  );
});

const getLabels = (getText) => ({
  valueBetweenErrorMessage: (min, max) => getText('uiInput.valueBetweenErrorMessage', { min, max }),
  valueGreaterThanOrEqualErrorMessage: (min) => getText('uiInput.valueGreaterThanOrEqualErrorMessage', { min }),
  valueLessThanOrEqualErrorMessage: (max) => getText('uiInput.valueLessThanOrEqualErrorMessage', { max }),
});

Input.propTypes = {
  type: PropTypes.string,
  label: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.elementType,
  ]),
  labelType: PropTypes.string,
  error: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.bool,
    PropTypes.string,
  ]),
  action: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.func,
    PropTypes.element,
    PropTypes.bool,
  ]),
  useLocalValue: PropTypes.bool,
  elementRight: PropTypes.oneOfType([
    PropTypes.element,
    PropTypes.bool,
  ]),
  elementLeft: PropTypes.oneOfType([
    PropTypes.element,
    PropTypes.bool,
  ]),
  classes: PropTypes.object,
  style: PropTypes.object,
  className: PropTypes.string,
  leftStyle: PropTypes.object,
  leftClassName: PropTypes.string,
  rightStyle: PropTypes.object,
  rightClassName: PropTypes.string,
  dataTestId: PropTypes.string,
  disabled: PropTypes.bool,
  containerStyle: PropTypes.object,
};

export default Input;
